<template>
    <div id="clinic-invoice-form-modal">
        <modal :backdrop="false" size="lg" v-model="modal" append-to-body>
            <div slot="header">
                <button @click.prevent="closeModal" class="close" type="button">×</button>
                <h4 class="smalltitle">{{ isEdit ? 'Edit invoice draft' : 'Add invoice' }} — {{ clinic.fullName }}</h4>
            </div>
            <div class="block-body">
                <div class="invoice-meta">
                    <div class="meta-label">Invoice will send to email</div>
                    <div class="meta-value"><strong>{{ email || clinic.email }}</strong></div>
                </div>
                <div class="invoice-meta">
                    <div class="meta-label">Period</div>
                    <div class="meta-value">
                        <input type="month" v-model="form.period" class="form-control">
                    </div>
                </div>

                <table class="table invoice-items">
                    <thead>
                    <tr>
                        <th>Description</th>
                        <th class="col-qty">Qty</th>
                        <th class="col-price">Unit price</th>
                        <th class="col-comp">Complimentary</th>
                        <th class="col-amount">Amount (USD)</th>
                        <th class="col-remove"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="isLoadingDefaults">
                        <td colspan="6" class="loading"><i class="fas fa-spinner fa-spin"></i></td>
                    </tr>
                    <tr v-else v-for="(item, index) in form.items" :key="`item-${index}`">
                        <td>
                            <input type="text" v-model="item.label" maxlength="120" class="form-control"
                                   placeholder="e.g. EMR subscription">
                            <div class="item-preview" v-if="describe(item) !== item.label.trim() || item.counted">
                                <span v-if="describe(item) !== item.label.trim()">{{ describe(item) }}</span>
                                <span class="item-hint" v-if="item.counted">
                                    <i class="fa fa-info-circle" :title="item.counted.join('\n') || 'No provider'"></i>
                                </span>
                            </div>
                        </td>
                        <td class="col-qty">
                            <input type="number" v-model="item.qty" min="0" step="1" class="form-control">
                        </td>
                        <td class="col-price"><input type="number" v-model="item.unit_price" min="0" step="0.01"
                                                     class="form-control" placeholder="0.00"></td>
                        <td class="col-comp"><input type="checkbox" v-model="item.complimentary"></td>
                        <td class="col-amount">{{ formatMoney(amountCents(item)) }}</td>
                        <td class="col-remove">
                            <a href="javascript:void(0)" v-if="form.items.length > 1" @click.prevent="removeItem(index)"
                               title="Remove"><i class="fa fa-times"></i></a>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colspan="2"><a href="javascript:void(0)" @click.prevent="addItem"><i class="fa fa-plus"></i> Add item</a></td>
                        <td colspan="3" class="total"><strong>Total: {{ formatMoney(totalCents) }}</strong></td>
                        <td></td>
                    </tr>
                    </tfoot>
                </table>

                <div class="prolabel note">Internal note</div>
                <div class="profield">
                    <textarea v-model="form.note" rows="2" class="form-control"
                              placeholder="Only visible to admins"></textarea>
                </div>
            </div>
            <div slot="footer">
                <button type="button" class="btn btn-default" :disabled="isProcessing || isLoadingDefaults" @click="save">
                    <i class="fas fa-spinner fa-spin" v-if="isProcessing"></i> Save draft
                </button>
                <button @click.prevent="closeModal" class="btn btn-default" type="button">Cancel</button>
            </div>
        </modal>
    </div>
</template>

<script>
import httpClient from '../../../client'
import moment from 'moment'
import CommonMixin from '../../../mixins/general/CommonMixin.vue'
import ClinicInvoiceMixin from '../../../mixins/user/ClinicInvoiceMixin.vue'

// Dòng mặc định khi tạo invoice; countKey = danh sách provider trả về từ /clinic-invoices/defaults
const PRESETS = [
  { key: 'emr_erx', label: 'WikiAllergies EMR Pro with E-prescribe', rate: '/provider/month', unit: 'Provider', countKey: 'eRx' },
  { key: 'epcs', label: 'EPCS Service', rate: ' per year per provider', unit: 'Provider', countKey: 'ePcs' },
  { key: 'fax', label: 'Electronic Fax and SMS', rate: ' per number/clinic/month', unit: '' }
]

function emptyItem (data) {
  return {
    key: null,
    label: '',
    rate: '',
    unit: '',
    detail: '',
    qty: 1,
    unit_price: '',
    complimentary: false,
    counted: null, // tên provider được đếm (tooltip)
    ...data
  }
}

/** Item đã lưu (amount, unit_price tính bằng cent) -> dòng trên form. Item cũ chỉ có description + amount. */
function fromSaved (saved) {
  const unitPrice = saved.unit_price !== undefined ? saved.unit_price : saved.amount
  return emptyItem({
    key: saved.key || null,
    label: saved.label || saved.description,
    rate: saved.rate || '',
    unit: saved.unit || '',
    detail: saved.detail || '',
    qty: saved.qty !== undefined ? saved.qty : 1,
    unit_price: (unitPrice / 100).toFixed(2),
    complimentary: !!saved.complimentary
  })
}

export default {
  name: 'ClinicInvoiceFormModal',
  props: {
    clinic: { type: Object, required: true },
    // null = tạo mới
    invoice: { type: Object, default: null },
    // email đang nhập ở ô "Invoice email" của popup Invoices -> lưu vào invoice
    email: { type: String, default: '' }
  },
  mixins: [CommonMixin, ClinicInvoiceMixin],
  data () {
    return {
      modal: false,
      isProcessing: false,
      isLoadingDefaults: false,
      form: {
        period: moment().format('YYYY-MM'),
        items: [emptyItem()],
        note: ''
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.invoice
    },
    totalCents () {
      return this.form.items.reduce((sum, item) => sum + this.amountCents(item), 0)
    }
  },
  watch: {
    modal (value) {
      this.$emit('updateModal', value)
    }
  },
  mounted () {
    if (this.invoice) {
      this.form.period = this.invoice.period
      this.form.items = this.invoice.items.map(fromSaved)
      this.form.note = this.invoice.note || ''
    } else {
      this.loadDefaults()
    }
    this.modal = true
  },
  methods: {
    /** Dựng các dòng PRESETS, qty của EMR/EPCS đếm theo trạng thái hiện tại. */
    async loadDefaults () {
      this.isLoadingDefaults = true
      let defaults = { eRx: [], ePcs: [], fax: '' }
      try {
        const response = await httpClient.get(`/clinic-invoices/defaults`, { params: { clinic_id: this.clinic.id } })
        if (response.data.s === 200) {
          defaults = response.data.data
        } else {
          this.alertError(response.data.msg)
        }
      } catch (e) {
        this.alertException(e)
      }
      this.form.items = PRESETS.map(preset => {
        const item = emptyItem({ key: preset.key, label: preset.label, rate: preset.rate, unit: preset.unit })
        if (preset.countKey) {
          item.counted = defaults[preset.countKey] || []
          item.qty = item.counted.length
        }
        if (preset.key === 'fax' && defaults.fax) {
          item.detail = `Fax ${defaults.fax}`
        }
        return item
      })
      this.isLoadingDefaults = false
    },
    amountCents (item) {
      if (item.complimentary) return 0
      return Math.round((Number(item.qty) || 0) * (Number(item.unit_price) || 0) * 100)
    },
    /** Dòng gửi lên Stripe, vd "WikiAllergies EMR Pro with E-prescribe ($500/provider/month) x 3 Providers". */
    describe (item) {
      const qty = Number(item.qty) || 0
      const price = Number(item.unit_price) || 0
      let text = item.label.trim()
      if (item.rate && price > 0) {
        text += ` ($${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}${item.rate})`
      }
      if (item.unit) {
        text += ` x ${qty} ${item.unit}${qty === 1 ? '' : 's'}`
      } else if (qty !== 1) {
        text += ` x ${qty}`
      }
      if (item.detail) text += ` — ${item.detail}`
      if (item.complimentary) text += ' (complimentary)'
      return text
    },
    addItem () {
      this.form.items.push(emptyItem())
    },
    removeItem (index) {
      this.form.items.splice(index, 1)
    },
    async save () {
      if (this.isProcessing) return
      this.isProcessing = true
      try {
        const payload = {
          clinic_id: this.clinic.id,
          period: this.form.period,
          items: this.form.items.map(item => ({
            key: item.key,
            label: item.label,
            rate: item.rate,
            unit: item.unit,
            detail: item.detail,
            qty: Number(item.qty) || 0,
            unit_price: Number(item.unit_price) || 0,
            complimentary: item.complimentary,
            description: this.describe(item),
            amount: (this.amountCents(item) / 100).toFixed(2)
          })),
          note: this.form.note,
          email: this.email
        }
        const response = this.isEdit
          ? await httpClient.put(`/clinic-invoices/${this.invoice.id}`, payload)
          : await httpClient.post(`/clinic-invoices`, payload)
        if (response.data.s === 200) {
          this.alertSuccess(response.data.msg)
          this.$emit('saved', response.data.data)
          this.modal = false
          return
        }
        this.alertError(response.data.msg)
      } catch (e) {
        this.alertException(e)
      } finally {
        this.isProcessing = false
      }
    },
    closeModal () {
      this.modal = false
    }
  }
}
</script>

<style scoped>
    .invoice-meta {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
    }

    .invoice-meta .meta-label {
        width: 140px;
        flex-shrink: 0;
    }

    .invoice-meta .meta-value {
        flex: 1;
    }

    .invoice-meta .meta-value .form-control {
        max-width: 240px;
    }

    .invoice-items > thead > tr > th {
        vertical-align: top;
    }

    .invoice-items .col-qty {
        width: 90px;
    }

    .invoice-items .col-price {
        width: 130px;
    }

    .invoice-items .col-comp {
        width: 130px;
        text-align: center;
    }

    .invoice-items .col-amount {
        width: 120px;
        text-align: right;
    }

    .invoice-items .col-remove {
        width: 40px;
        text-align: center;
    }

    .invoice-items td.col-comp,
    .invoice-items td.col-amount,
    .invoice-items td.col-remove {
        vertical-align: middle;
    }

    .invoice-items .total {
        text-align: right;
    }

    .invoice-items .loading {
        text-align: center;
        color: #d9df20;
    }

    .invoice-items .item-preview {
        margin-top: 4px;
        font-size: 12px;
        color: #888;
    }

    .invoice-items .item-hint .fa {
        cursor: help;
        margin-left: 4px;
    }
</style>
